/** Shared Tailwind design tokens for runtime TypeScript usage. */

export const SPACING_RULES = {
  xxxs: '4px',
  xxs: '8px',
  xs: '12px',
  s: '16px',
  m: '20px',
  l: '24px',
  xl: '32px',
  xxl: '40px',
  xxxl: '48px',
  none: '0px',
} as const

export const BORDER_RADIUS_RULES = {
  xs: '4px',
  sm: '8px',
  m: '12px',
  lg: '16px',
  xl: '20px',
  none: '0px',
} as const

export const FONT_SIZE_RULES = {
  xxs: ['10px'],
  xs: ['11px', '14px'],
  s: ['12px', '16px'],
  sm: ['13px', '16px'],
  base: ['15px', '20px'],
  lg: ['17px', '24px'],
  xl: ['20px', '28px'],
  '2xl': ['24px', '32px'],
  '3xl': ['28px'],
  '4xl': ['32px'],
} as const

export const HEIGHT_RULES = {
  xxxs: '16px',
  xxs: '20px',
  xs: '24px',
  s: '32px',
  m: '40px',
  l: '48px',
  xl: '56px',
  xxl: '64px',
  xxxl: '96px',
} as const

export const SIZE_RULES = {
  xxxs: '16px',
  xxs: '20px',
  xs: '24px',
  s: '32px',
  m: '40px',
  l: '48px',
  xl: '56px',
  xxl: '64px',
  xxxl: '96px',
} as const

export const COLOR_RULES = {
  white: 'var(--white)',
  black: 'var(--black)',
  gray: {
    50: 'var(--gray-50)',
    100: 'var(--gray-100)',
    200: 'var(--gray-200)',
    300: 'var(--gray-300)',
    400: 'var(--gray-400)',
    500: 'var(--gray-500)',
    600: 'var(--gray-600)',
    700: 'var(--gray-700)',
    800: 'var(--gray-800)',
    900: 'var(--gray-900)',
    950: 'var(--gray-950)',
  },
  green: {
    50: 'var(--green-50)',
    100: 'var(--green-100)',
    200: 'var(--green-200)',
    300: 'var(--green-300)',
    400: 'var(--green-400)',
    500: 'var(--green-500)',
    600: 'var(--green-600)',
    700: 'var(--green-700)',
    800: 'var(--green-800)',
    900: 'var(--green-900)',
    950: 'var(--green-950)',
  },
  fuchsia: {
    50: 'var(--fuchsia-50)',
    100: 'var(--fuchsia-100)',
    200: 'var(--fuchsia-200)',
    300: 'var(--fuchsia-300)',
    400: 'var(--fuchsia-400)',
    500: 'var(--fuchsia-500)',
    600: 'var(--fuchsia-600)',
    700: 'var(--fuchsia-700)',
    800: 'var(--fuchsia-800)',
    900: 'var(--fuchsia-900)',
    950: 'var(--fuchsia-950)',
  },
  violet: {
    50: 'var(--violet-50)',
    100: 'var(--violet-100)',
    200: 'var(--violet-200)',
    300: 'var(--violet-300)',
    400: 'var(--violet-400)',
    500: 'var(--violet-500)',
    600: 'var(--violet-600)',
    700: 'var(--violet-700)',
    800: 'var(--violet-800)',
    900: 'var(--violet-900)',
    950: 'var(--violet-950)',
  },
  pink: {
    50: 'var(--pink-50)',
    100: 'var(--pink-100)',
    200: 'var(--pink-200)',
    300: 'var(--pink-300)',
    400: 'var(--pink-400)',
    500: 'var(--pink-500)',
    600: 'var(--pink-600)',
    700: 'var(--pink-700)',
    800: 'var(--pink-800)',
    900: 'var(--pink-900)',
    950: 'var(--pink-950)',
  },
  cyan: {
    50: 'var(--cyan-50)',
    100: 'var(--cyan-100)',
    200: 'var(--cyan-200)',
    300: 'var(--cyan-300)',
    400: 'var(--cyan-400)',
    500: 'var(--cyan-500)',
    600: 'var(--cyan-600)',
    700: 'var(--cyan-700)',
    800: 'var(--cyan-800)',
    900: 'var(--cyan-900)',
    950: 'var(--cyan-950)',
  },
  yellow: {
    50: 'var(--yellow-50)',
    100: 'var(--yellow-100)',
    200: 'var(--yellow-200)',
    300: 'var(--yellow-300)',
    400: 'var(--yellow-400)',
    500: 'var(--yellow-500)',
    600: 'var(--yellow-600)',
    700: 'var(--yellow-700)',
    800: 'var(--yellow-800)',
    900: 'var(--yellow-900)',
    950: 'var(--yellow-950)',
  },
  red: {
    50: 'var(--red-50)',
    100: 'var(--red-100)',
    200: 'var(--red-200)',
    300: 'var(--red-300)',
    400: 'var(--red-400)',
    500: 'var(--red-500)',
    600: 'var(--red-600)',
    700: 'var(--red-700)',
    800: 'var(--red-800)',
    900: 'var(--red-900)',
    950: 'var(--red-950)',
  },
  blue: {
    50: 'var(--blue-50)',
    100: 'var(--blue-100)',
    200: 'var(--blue-200)',
    300: 'var(--blue-300)',
    400: 'var(--blue-400)',
    500: 'var(--blue-500)',
    600: 'var(--blue-600)',
    700: 'var(--blue-700)',
    800: 'var(--blue-800)',
    900: 'var(--blue-900)',
    950: 'var(--blue-950)',
  },
  'alpha-white': {
    10: 'var(--alpha-white-10)',
    20: 'var(--alpha-white-20)',
    30: 'var(--alpha-white-30)',
    40: 'var(--alpha-white-40)',
    50: 'var(--alpha-white-50)',
    60: 'var(--alpha-white-60)',
    70: 'var(--alpha-white-70)',
    80: 'var(--alpha-white-80)',
    90: 'var(--alpha-white-90)',
  },
  'alpha-black': {
    10: 'var(--alpha-black-10)',
    20: 'var(--alpha-black-20)',
    30: 'var(--alpha-black-30)',
    40: 'var(--alpha-black-40)',
    50: 'var(--alpha-black-50)',
    60: 'var(--alpha-black-60)',
    70: 'var(--alpha-black-70)',
    80: 'var(--alpha-black-80)',
    90: 'var(--alpha-black-90)',
  },
} as const

export type SpacingKey = keyof typeof SPACING_RULES
export type RadiusKey = keyof typeof BORDER_RADIUS_RULES
export type FontSizeKey = keyof typeof FONT_SIZE_RULES
export type HeightKey = keyof typeof HEIGHT_RULES
export type SizeKey = keyof typeof SIZE_RULES
